import { useCallback, useRef, useState } from 'react';
import * as API from '../utils/api';
import { useMounted } from './useMounted';
import { NetworkError, NetworkPromise, NetworkRequestConfig, NetworkResponse } from '@packages/dataFetch';

type MethodType = 'POST' | 'GET';
export type FetchRequestConfig = Partial<
	NetworkRequestConfig & {
		methodType: MethodType;
		cancelId?: string;
	}
>;

type Config = {
	delayedPendingTime?: number;
	method: MethodType;
};

export function useFetch<ResponseData>(url: string, config?: Config) {
	const configRef = useRef(config);
	const delayTimerIdRef = useRef<NodeJS.Timeout>();
	const { mountedRef } = useMounted();
	const [isPending, setIsPending] = useState(false);
	const [fetched, setFetched] = useState(false);
	const [isDelayedPending, setIsDelayedPending] = useState(false);
	const [error, setError] = useState<NetworkError | null>(null);
	const [response, setResponse] = useState<NetworkResponse<ResponseData>>({} as NetworkResponse<ResponseData>);

	const fetch = useCallback(
		(data?, requestConfig: FetchRequestConfig = {}): NetworkPromise<ResponseData> => {
			setIsPending(true);
			setError(null);
			delayTimerIdRef.current = setTimeout(() => {
				if (!mountedRef.current) {
					return;
				}

				setIsDelayedPending(true);
			}, configRef.current?.delayedPendingTime || 700);
			const methodName: MethodType = configRef.current?.method || requestConfig.methodType || 'GET';
			const methodType = API[methodName];

			return methodType(url, data, requestConfig)
				.then((response) => {
					clearTimeout(delayTimerIdRef.current!);

					if (!mountedRef.current) {
						return;
					}

					setIsPending(false);
					setIsDelayedPending(false);
					setResponse(response);
					setFetched(true);

					return response as NetworkResponse<ResponseData>;
				})
				.catch((error) => {
					if (!mountedRef.current) {
						return Promise.reject(error);
					}

					setError(error);
					setIsPending(false);

					return Promise.reject(error);
				});
		},
		[mountedRef, url],
	);

	const resetResponse = useCallback(() => {
		setResponse({} as NetworkResponse<ResponseData>);
	}, []);

	return {
		isPending,
		isDelayedPending,
		fetch,
		response,
		resetResponse,
		fetched,
		error,
	};
}

export default useFetch;
